import { useEffect, useRef, useState, type CSSProperties } from "react";

/**
 * Art for the login page: the six school stickers (from the supplied SVG) and an animated mascot.
 *
 * The mascot is cut into layers so it can react: body, a waving hand, and two eyes (a white eye-opening mask, a moving iris
 * and eyelids). Files live in public/assets/mascot/. All motion is switched off for visitors who prefer reduced motion.
 */

// ---------- stickers ----------
const SPRITE = `<svg xmlns="http://www.w3.org/2000/svg" width="0" height="0" style="position:absolute" aria-hidden="true" focusable="false"><style>.li-outline{stroke:#5A2B18;stroke-width:18;stroke-linecap:round;stroke-linejoin:round}.li-fine{stroke:#8A4A2A;stroke-width:8;stroke-linecap:round;stroke-linejoin:round}.li-orange{fill:#F97316}.li-orange2{fill:#FB923C}.li-cream{fill:#FFF8ED}.li-white{fill:#FFFFFF}.li-brown{fill:#5A2B18}.li-gold{fill:#FDBA45}.li-pale{fill:#F4D9BE}</style><defs><linearGradient id="li-orangeGrad" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#FDBA45"/><stop offset="1" stop-color="#F97316"/></linearGradient><linearGradient id="li-coinGrad" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#FFE28A"/><stop offset="1" stop-color="#F59E0B"/></linearGradient></defs><symbol id="li-cap" viewBox="89 144 441 359"><g transform="translate(70 95)">
    <path class="li-brown li-outline" d="M35 160 L225 65 L425 160 L230 260 Z"/>
    <path class="li-orange li-outline" d="M93 190 L230 262 L365 192 L365 255 Q230 345 93 255 Z"/>
    <path class="li-cream li-outline" d="M105 193 L230 254 L352 193 L352 221 Q230 300 105 221 Z"/>
    <path class="li-gold li-outline" d="M422 162 L422 285"/>
    <circle cx="422" cy="302" r="16" class="li-gold li-outline"/>
    <path class="li-orange li-outline" d="M422 317 C397 348 397 376 405 392 L422 370 L439 392 C447 376 447 348 422 317 Z"/>
    <path d="M82 155 L224 88 L360 154" class="li-fine" opacity=".45"/>
  </g></symbol><symbol id="li-book" viewBox="559 184 462 300"><g transform="translate(555 110)">
    <path class="li-orange li-outline" d="M20 105 Q125 70 235 118 Q345 70 450 105 L450 345 Q345 310 235 358 Q125 310 20 345 Z"/>
    <path class="li-cream li-outline" d="M42 117 Q135 91 225 136 L225 321 Q135 278 42 307 Z"/>
    <path class="li-white li-outline" d="M245 136 Q335 91 428 117 L428 307 Q335 278 245 321 Z"/>
    <path d="M235 120 L235 350" class="li-fine"/>
    <path d="M76 151 Q135 136 186 157 M76 189 Q135 174 186 195 M76 227 Q135 212 186 233 M284 157 Q337 136 395 151 M284 195 Q337 174 395 189 M284 233 Q337 212 395 227" class="li-fine" opacity=".65"/>
    <path d="M48 315 Q137 290 222 330 M248 330 Q333 290 422 315" class="li-fine" opacity=".45"/>
  </g></symbol><symbol id="li-pencil" viewBox="1069 83 375 394"><g transform="translate(1125 90) rotate(42 145 175)">
    <rect x="83" y="32" width="125" height="300" rx="18" class="li-orange li-outline"/>
    <path d="M83 68 H208 V268 H83 Z" fill="url(#li-orangeGrad)" class="li-outline"/>
    <path d="M83 88 H208" class="li-fine" opacity=".6"/>
    <path d="M83 267 H208 V310 L145 382 L83 310 Z" class="li-cream li-outline"/>
    <path d="M116 345 L145 382 L174 345" class="li-fine"/>
    <rect x="83" y="8" width="125" height="58" rx="16" class="li-white li-outline"/>
    <path d="M92 28 H199" class="li-fine" opacity=".5"/>
    <path d="M111 92 V240" stroke="#FFD18A" stroke-width="13" stroke-linecap="round"/>
  </g></symbol><symbol id="li-calendar" viewBox="84 594 442 362"><g transform="translate(75 585)">
    <rect x="25" y="40" width="410" height="315" rx="34" class="li-cream li-outline"/>
    <path d="M25 75 Q25 40 60 40 H400 Q435 40 435 75 V130 H25 Z" class="li-orange li-outline"/>
    <path d="M112 25 V73 M345 25 V73" class="li-brown li-outline"/>
    <rect x="72" y="155" width="65" height="55" rx="10" class="li-pale"/>
    <rect x="166" y="155" width="65" height="55" rx="10" class="li-pale"/>
    <rect x="260" y="155" width="65" height="55" rx="10" class="li-pale"/>
    <rect x="354" y="155" width="40" height="55" rx="10" class="li-pale"/>
    <rect x="72" y="235" width="65" height="55" rx="10" class="li-pale"/>
    <rect x="166" y="235" width="65" height="55" rx="10" class="li-pale"/>
    <rect x="260" y="235" width="65" height="55" rx="10" class="li-orange"/>
    <path d="M274 262 L291 278 L315 249" stroke="#FFFFFF" stroke-width="12" stroke-linecap="round" stroke-linejoin="round"/>
    <rect x="354" y="235" width="40" height="55" rx="10" class="li-pale"/>
    <path d="M64 326 Q100 305 136 326" class="li-fine" opacity=".35"/>
  </g></symbol><symbol id="li-coin" viewBox="609 594 382 382"><g transform="translate(595 590)">
    <circle cx="205" cy="195" r="175" fill="url(#li-coinGrad)" class="li-outline"/>
    <circle cx="205" cy="195" r="137" class="li-orange li-outline"/>
    <circle cx="205" cy="195" r="112" fill="url(#li-orangeGrad)" stroke="#B95A20" stroke-width="8"/>
    <path d="M151 120 H265 Q278 120 278 134 Q278 148 265 148 H231 Q257 166 258 190 H278 Q291 190 291 204 Q291 218 278 218 H255 Q242 269 183 287 L247 342 Q260 353 248 365 Q237 376 225 365 L139 292 Q129 284 136 272 Q141 264 151 264 H170 Q209 264 224 218 H151 Q138 218 138 204 Q138 190 151 190 H224 Q219 164 190 148 H151 Q138 148 138 134 Q138 120 151 120 Z" fill="#FFF8ED" transform="translate(0 -20) scale(.88 1) translate(25 0)"/>
    <path d="M105 88 Q140 55 180 54" stroke="#FFFFFF" stroke-width="12" stroke-linecap="round" opacity=".5"/>
  </g></symbol><symbol id="li-chart" viewBox="1104 586 392 355"><g transform="translate(1090 585)">
    <path d="M30 340 H390" class="li-brown li-outline"/>
    <rect x="55" y="235" width="82" height="105" rx="13" fill="url(#li-orangeGrad)" class="li-outline"/>
    <rect x="170" y="175" width="82" height="165" rx="13" fill="url(#li-orangeGrad)" class="li-outline"/>
    <rect x="285" y="105" width="82" height="235" rx="13" fill="url(#li-orangeGrad)" class="li-outline"/>
    <path d="M45 200 Q140 178 210 110 Q265 60 337 22" stroke="#5A2B18" stroke-width="34" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M45 200 Q140 178 210 110 Q265 60 337 22" stroke="#F97316" stroke-width="20" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M291 24 L342 17 L334 68" stroke="#5A2B18" stroke-width="30" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M291 24 L342 17 L334 68" stroke="#F97316" stroke-width="18" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M73 255 V315 M188 197 V315 M303 128 V315" stroke="#FFD18A" stroke-width="10" stroke-linecap="round" opacity=".75"/>
  </g></symbol></svg>`;
const STICKER_SIZE = {"cap": [441, 359], "book": [462, 300], "pencil": [375, 394], "calendar": [442, 362], "coin": [382, 382], "chart": [392, 355]} as const;
export type StickerName = keyof typeof STICKER_SIZE;

/** Renders the sprite once (hidden). Put it anywhere in the page. */
export function LoginIconSprite() {
  return <span aria-hidden style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }} dangerouslySetInnerHTML={{ __html: SPRITE }} />;
}

/** One sticker; `size` is its height in CSS units. */
export function Sticker({ name, size = "2.5rem", className, style }: { name: StickerName; size?: string; className?: string; style?: CSSProperties }) {
  const [w, h] = STICKER_SIZE[name];
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} className={className} style={{ height: size, width: "auto", aspectRatio: `${w} / ${h}`, display: "block", overflow: "visible", ...style }} aria-hidden focusable="false">
      <use href={`#li-${name}`} />
    </svg>
  );
}

// ---------- mascot ----------
export type MascotMode = "idle" | "email" | "password" | "peek" | "loading" | "error";

const ASSETS = `${import.meta.env.BASE_URL}assets/mascot/`;
const W = 506, H = 1000;
const HAND = {"x": -1, "y": 227, "w": 140, "h": 142, "px": 76, "py": 133};
const EYES = { L: {"x": 195, "y": 211, "w": 57, "h": 57}, R: {"x": 306, "y": 230, "w": 66, "h": 59} };
const IRIS = { L: {"x": 211, "y": 212, "w": 40, "h": 55}, R: {"x": 315, "y": 233, "w": 48, "h": 55} };
const SKIN = "#fbc087";

const pctX = (v: number) => `${(v / W) * 100}%`;
const pctY = (v: number) => `${(v / H) * 100}%`;
const BUBBLE: Record<MascotMode, string[]> = {
  idle: ["Hi there! 👋", "Ready to log in?", "Welcome back!"],
  email: ["Type your school email ✉️"],
  password: ["No peeking! 🙈"],
  peek: ["Okay, I'm looking 👀"],
  loading: ["Checking… ⏳"],
  error: ["Oops! Check your details 😅"],
};

const CSS = `
.lgm { position: relative; aspect-ratio: ${W} / ${H}; height: var(--mh, 24rem); --u: calc(var(--mh, 24rem) / ${H}); --lx: 0; --ly: 0; --lean: 0; }
.lgm-figure { position: absolute; inset: 0; transform-origin: 50% 100%; transform: rotate(calc(var(--lean) * 1deg)); transition: transform .35s ease-out; }
.lgm-bob { position: absolute; inset: 0; transform-origin: 50% 100%; }
.lgm img.lgm-layer { position: absolute; display: block; user-select: none; pointer-events: none; -webkit-user-drag: none; }
.lgm-eye { position: absolute; overflow: hidden; -webkit-mask-size: 100% 100%; mask-size: 100% 100%; -webkit-mask-repeat: no-repeat; mask-repeat: no-repeat; }
.lgm-sclera { position: absolute; inset: 0; background: radial-gradient(ellipse at 62% 45%, #ffffff 55%, #e7dff0 100%); }
.lgm-scan { position: absolute; inset: 0; }
.lgm-iris { position: absolute; transform: translate(calc(var(--u) * var(--lx)), calc(var(--u) * var(--ly))); transition: transform .14s ease-out; }
.lgm-lid { position: absolute; inset: -2px; background: ${SKIN}; transform: scaleY(0); transform-origin: 50% 0; }
.lgm-lid.closed { transform: scaleY(1); transition: transform .22s ease-in; }
.lgm-lid.blink { }
.lgm-bubble { position: absolute; left: 80%; top: 2%; width: max-content; max-width: 11rem; padding: .45rem .75rem; border-radius: 1rem 1rem 1rem .25rem; background: #fff; color: #7c2d12; font: 600 .75rem/1.25 system-ui, sans-serif; box-shadow: 0 8px 22px -8px rgba(124,45,18,.45); pointer-events: none; z-index: 3; }
.lgm-hand { transform-origin: ${(HAND.px / HAND.w) * 100}% ${(HAND.py / HAND.h) * 100}%; }
@media (prefers-reduced-motion: no-preference) {
  @keyframes lgm-bob { 0%,100% { transform: translateY(0) } 50% { transform: translateY(calc(var(--u) * -7)) } }
  @keyframes lgm-wave { 0%,22%,100% { transform: rotate(0) } 3% { transform: rotate(-16deg) } 7% { transform: rotate(12deg) } 11% { transform: rotate(-14deg) } 15% { transform: rotate(10deg) } 19% { transform: rotate(-6deg) } }
  @keyframes lgm-wave-fast { 0%,100% { transform: rotate(0) } 20% { transform: rotate(-18deg) } 45% { transform: rotate(14deg) } 70% { transform: rotate(-14deg) } }
  @keyframes lgm-blink { 0%,92%,100% { transform: scaleY(0) } 95.5% { transform: scaleY(1) } }
  @keyframes lgm-shake { 0%,100% { transform: translateX(0) rotate(0) } 18% { transform: translateX(calc(var(--u) * -10)) rotate(-2.5deg) } 40% { transform: translateX(calc(var(--u) * 9)) rotate(2deg) } 62% { transform: translateX(calc(var(--u) * -6)) rotate(-1.2deg) } 82% { transform: translateX(calc(var(--u) * 3)) } }
  @keyframes lgm-scan { 0%,100% { transform: translateX(calc(var(--u) * -5)) } 50% { transform: translateX(calc(var(--u) * 5)) } }
  @keyframes lgm-pop { from { opacity: 0; transform: translateY(6px) scale(.9) } to { opacity: 1; transform: none } }
  @keyframes lgm-hop { 0%,100% { transform: translateY(0) } 40% { transform: translateY(calc(var(--u) * -22)) } 70% { transform: translateY(0) } 85% { transform: translateY(calc(var(--u) * -6)) } }
  .lgm-bob { animation: lgm-bob 4.6s ease-in-out infinite; }
  .lgm-hand { animation: lgm-wave 8s ease-in-out 1s infinite; }
  .lgm:hover .lgm-hand { animation: lgm-wave-fast .9s ease-in-out 2; }
  .lgm-lid.blink { animation: lgm-blink 5.2s ease-in-out infinite; }
  .lgm.is-error .lgm-bob { animation: lgm-shake .5s ease-in-out; }
  .lgm.is-error .lgm-hand, .lgm.is-password .lgm-hand { animation: none; }
  .lgm.is-loading .lgm-scan { animation: lgm-scan 1.1s ease-in-out infinite; }
  .lgm.is-peek .lgm-bob { animation: lgm-hop .6s ease-out 1, lgm-bob 4.6s ease-in-out .6s infinite; }
  .lgm-bubble > span { display: inline-block; animation: lgm-pop .3s ease-out both; }
}
`;

const LOOK: Partial<Record<MascotMode, [number, number]>> = { peek: [5.5, 3.5], loading: [0, 0], error: [4, 3] };

/** Mascot that follows the pointer with his eyes, blinks, waves, leans, and reacts to the form (`mode`). */
export function AnimatedMascot({ mode = "idle", emailLength = 0, height = "24rem", message, className }: {
  mode?: MascotMode;
  /** Characters typed in the email field, so the eyes can read along. */
  emailLength?: number;
  /** CSS height of the figure, e.g. "25rem" or "min(25rem, 52vh)". */
  height?: string;
  /** Override the speech bubble text. */
  message?: string;
  className?: string;
}) {
  const root = useRef<HTMLDivElement>(null);
  const modeRef = useRef(mode);
  modeRef.current = mode;

  const setLook = (x: number, y: number) => {
    root.current?.style.setProperty("--lx", x.toFixed(2));
    root.current?.style.setProperty("--ly", y.toFixed(2));
  };

  // Eyes (and a slight lean) follow the pointer while idle.
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let raf = 0;
    const onMove = (e: PointerEvent) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = root.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const dx = e.clientX - (r.left + r.width * 0.48);
        const dy = e.clientY - (r.top + r.height * 0.26);
        const dist = Math.hypot(dx, dy) || 1;
        const f = Math.min(1, dist / 280);
        el.style.setProperty("--lean", String(Math.max(-1, Math.min(1, dx / 500)) * 2.5));
        if (modeRef.current === "idle") setLook((dx / dist) * f * 5.5, (dy / dist) * f * 4);
      });
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    return () => { window.removeEventListener("pointermove", onMove); cancelAnimationFrame(raf); };
  }, []);

  // Look somewhere fixed for the non-idle modes; the email mode reads along as you type.
  useEffect(() => {
    if (mode === "email") setLook(2.5 + (Math.min(emailLength, 24) / 24) * 3, 1.5);
    else if (LOOK[mode]) setLook(...(LOOK[mode] as [number, number]));
    else if (mode === "password") setLook(0, 1);
  }, [mode, emailLength]);

  // Idle greetings rotate.
  const [greet, setGreet] = useState(0);
  useEffect(() => {
    if (mode !== "idle") return;
    const id = window.setInterval(() => setGreet((g) => g + 1), 4200);
    return () => window.clearInterval(id);
  }, [mode]);

  const lines = BUBBLE[mode];
  const text = message ?? lines[greet % lines.length];
  const closed = mode === "password";

  const eye = (k: "L" | "R") => {
    const e = EYES[k], i = IRIS[k];
    return (
      <div key={k} className="lgm-eye" style={{ left: pctX(e.x), top: pctY(e.y), width: pctX(e.w), height: pctY(e.h), WebkitMaskImage: `url(${ASSETS}eye${k}-mask.png)`, maskImage: `url(${ASSETS}eye${k}-mask.png)` }}>
        <div className="lgm-sclera" />
        <div className="lgm-scan">
          <img className="lgm-iris" alt="" draggable={false} src={`${ASSETS}iris${k}.png`} style={{ left: `${((i.x - e.x) / e.w) * 100}%`, top: `${((i.y - e.y) / e.h) * 100}%`, width: `${(i.w / e.w) * 100}%`, height: `${(i.h / e.h) * 100}%` }} />
        </div>
        <div className={`lgm-lid blink`} />
        <div className={`lgm-lid ${closed ? "closed" : ""}`} />
      </div>
    );
  };

  return (
    <div ref={root} className={`lgm is-${mode} ${className ?? ""}`} style={{ ["--mh" as string]: height } as CSSProperties} aria-hidden>
      <style>{CSS}</style>
      <div className="lgm-figure">
        <div className="lgm-bob">
          <img className="lgm-layer lgm-hand" alt="" draggable={false} src={`${ASSETS}hand.webp`} style={{ left: pctX(HAND.x), top: pctY(HAND.y), width: pctX(HAND.w), height: pctY(HAND.h) }} />
          <img className="lgm-layer" alt="" draggable={false} src={`${ASSETS}body.webp`} style={{ inset: 0, width: "100%", height: "100%" }} />
          {eye("L")}
          {eye("R")}
        </div>
      </div>
      <div className="lgm-bubble"><span key={`${mode}-${text}`}>{text}</span></div>
    </div>
  );
}
